import { ChevronDown } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

type FaqItem = {
  /** sufixo da chave i18n (e index visual): 'q0', 'q1'... */
  k: string;
  question: string;
  /** JSX default em PT-BR — usado pelo <Trans> como fallback (retrocompat). */
  answer: React.ReactNode;
};

/**
 * FAQ de pontuação. Espelha o padrão do `SubscriptionFAQ` (accordion nativo
 * <details>, namespace `pool`, fallback PT-BR inline via <Trans>). Reutilizado
 * na landing (`#pontuacao`) e no guia "Como Jogar".
 *
 * Cabeçalho em <div> (não <h2>/<p>) de propósito: assim o componente fica imune
 * aos seletores `prose` (`[&_h2]`, `[&_p]`) da página ComoJogar e renderiza
 * igual nos dois lugares.
 */
const ITEMS: FaqItem[] = [
  {
    k: 'q0',
    question: 'A pontuação é cumulativa? As faixas de pontos somam?',
    answer: (
      <>
        <strong>Não.</strong> Em cada partida vale apenas a <strong>melhor faixa</strong> que o seu palpite atingiu — os
        pontos das faixas não se somam. Ex.: na Regra C, se você acertou o vencedor (5 pts) e ainda cravou os gols de um
        dos times (2 pts), você leva <strong>5</strong>, e não 7.
      </>
    ),
  },
  {
    k: 'q1',
    question: 'Palpitei 1×0 e o jogo terminou 2×0 na Regra C. Por que ganhei 5 pontos e não 7?',
    answer: (
      <>
        Porque você <strong>acertou o vencedor</strong> (vitória da casa), e isso já vale 5 pontos — a melhor faixa que o
        seu palpite alcançou. O bônus de 2 pontos por "gols de um time" <strong>não soma</strong> com o vencedor: ele só
        entra quando você erra quem venceu, mas crava o número de gols de um dos times. Como você acertou o vencedor, a
        pontuação para nos 5.
      </>
    ),
  },
  {
    k: 'q2',
    question: 'Quando ganho os 2 pontos de "gols de um time" na Regra C?',
    answer: (
      <>
        Só quando você <strong>erra o vencedor</strong> da partida, mas acerta exatamente o número de gols de um dos
        times. Ex.: você palpitou 2×0 e o jogo terminou 0×0 — errou quem venceu, mas cravou os 0 gols do visitante, então
        leva 2 pontos. Se tivesse acertado o vencedor, já levaria os 5 (que vale mais).
      </>
    ),
  },
  {
    k: 'q3',
    question: 'Qual a diferença entre as Regras A, B e C?',
    answer: (
      <>
        <strong>Regra A (Simples):</strong> só o placar exato pontua (1 pt). <strong>Regra B (Intermediária):</strong>{' '}
        placar exato 3 pts e vencedor/empate correto 1 pt. <strong>Regra C (Avançada):</strong> placar exato 10 pts,
        vencedor/empate 5 pts e gols de um time 2 pts. Em todas, vale sempre a melhor faixa — nunca a soma.
      </>
    ),
  },
  {
    k: 'q4',
    question: 'O que significa "acertar o vencedor"?',
    answer: (
      <>
        É acertar <strong>quem ganhou a partida</strong> (ou que foi empate), mesmo errando o placar. Ex.: você palpitou
        2×1 e o jogo foi 3×0 — o placar está errado, mas você acertou que a casa venceu. Nas Regras B e C isso já garante
        pontos.
      </>
    ),
  },
  {
    k: 'q5',
    question: 'Quem escolhe a regra de pontuação do bolão?',
    answer: (
      <>
        O <strong>criador do bolão</strong> escolhe a regra (A, B ou C) ao criar o bolão, e ela vale do começo ao fim.
        Todos os participantes pontuam pela mesma regra.
      </>
    ),
  },
  {
    k: 'q6',
    question: 'Como funciona o desempate no ranking?',
    answer: (
      <>
        Quando dois participantes terminam com o mesmo total de pontos, o desempate segue esta ordem: 1) mais placares
        exatos; 2) mais vencedores corretos; 3) menor diferença agregada de gols; 4) ordem alfabética por nome.
      </>
    ),
  },
];

interface ScoringFAQProps {
  /** Classe extra no wrapper (ex.: margens específicas por página). */
  className?: string;
}

export default function ScoringFAQ({ className = '' }: ScoringFAQProps) {
  const { t } = useTranslation('pool');

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div role="heading" aria-level={2} className="text-base font-bold text-gray-900 mb-1">
        {t('scoring.faq.header_title', 'Dúvidas sobre a pontuação')}
      </div>
      <div className="text-xs text-gray-500 mb-4">
        {t('scoring.faq.header_subtitle', 'Toque em cada pergunta para ver a resposta.')}
      </div>

      <div className="divide-y divide-gray-100">
        {ITEMS.map((item) => (
          <details key={item.k} className="group py-3">
            <summary className="flex items-center justify-between cursor-pointer list-none gap-3">
              <span className="text-sm font-semibold text-gray-800">
                {t(`scoring.faq.${item.k}`, item.question)}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-2 text-sm text-gray-600 leading-relaxed">
              <Trans i18nKey={`scoring.faq.a${item.k.slice(1)}`} t={t}>
                {item.answer}
              </Trans>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
